/**
 * Plain text for paste into a textarea. Some formatting tools publish a broken `text/plain`
 * (full of U+FFFD replacement chars) while `text/html` still decodes emoji and Unicode via entities.
 */

function replacementCharCount(s: string): number {
  let n = 0;
  for (const ch of s) {
    if (ch === "\uFFFD") n++;
  }
  return n;
}

function extractTextFromClipboardHtml(html: string): string {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const body = doc.body;
    if (!body) return "";
    // innerText preserves line breaks between block nodes better than textContent for paste fragments.
    return body.innerText ?? body.textContent ?? "";
  } catch {
    return "";
  }
}

export function clipboardPlainTextBestEffort(data: DataTransfer | null): string {
  if (!data) return "";
  const plain = data.getData("text/plain");
  const htmlRaw = data.getData("text/html");
  const fromHtml = htmlRaw.trim() ? extractTextFromClipboardHtml(htmlRaw) : "";

  if (!plain.trim() && !fromHtml.trim()) return plain;
  if (!plain.trim()) return fromHtml;
  if (!fromHtml.trim()) return plain;

  const plainBad = replacementCharCount(plain);
  const htmlBad = replacementCharCount(fromHtml);
  if (htmlBad < plainBad) return fromHtml;

  return plain;
}

export function pastePlainIntoTextarea(
  el: HTMLTextAreaElement,
  pasted: string,
  setValue: (next: string) => void
): void {
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? 0;
  const prev = el.value;
  const next = prev.slice(0, start) + pasted + prev.slice(end);
  setValue(next);
  const caret = start + pasted.length;
  queueMicrotask(() => {
    el.focus();
    el.setSelectionRange(caret, caret);
  });
}
